import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Rating,
  Avatar,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PlaceIcon from '@mui/icons-material/Place';
import ExploreIcon from '@mui/icons-material/Explore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getDestinations, getDestinationTags } from '../services/destinationService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  borderRadius: 16,
  overflow: 'hidden',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)'
  }
}));

const DestinationsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Fetch destinations and tags on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Fetch all destinations
        const destinationsData = await getDestinations();
        setDestinations(destinationsData);
        
        // Fetch all tags
        const tagsData = await getDestinationTags();
        setAllTags(tagsData);
        
        setError(null);
      } catch (err) {
        console.error('Error loading destinations data:', err);
        setError('Failed to load destinations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Filter destinations based on search term and selected tags
  const filteredDestinations = destinations.filter(destination => {
    // Filter by search term
    const matchesSearch = destination.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          destination.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by selected tags
    const matchesTags = selectedTags.length === 0 || 
                        selectedTags.some(tag => destination.tags.includes(tag));
    
    return matchesSearch && matchesTags;
  });

  // Toggle tag selection
  const handleTagToggle = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // Handle card click to navigate to details
  const handleCardClick = (id) => {
    navigate(`/destinations/${id}`);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6, mt: { xs: 8, md: 10 } }}>
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Typography 
            variant="h3" 
            component="h1" 
            fontWeight="bold"
            gutterBottom
            sx={{
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' }
            }}
          >
            Explore Destinations
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 4,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }
            }}
          >
            Discover amazing places around the world and start planning your next adventure.
          </Typography>
          
          {/* Search bar */}
          <TextField
            fullWidth
            placeholder="Search destinations..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              maxWidth: 600, 
              mx: 'auto',
              backgroundColor: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <CircularProgress size={isMobile ? 40 : 48} />
          </Box>
        ) : error ? (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 5,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }
            }}
          >
            {error}
          </Alert>
        ) : (
          <>
            {/* Tags filter */}
            <Box sx={{ mb: 4, overflowX: 'auto', pb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: isMobile ? 'nowrap' : 'wrap', gap: 1, justifyContent: isMobile ? 'flex-start' : 'center', width: isMobile ? 'max-content' : '100%' }}>
                {allTags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    clickable
                    color={selectedTags.includes(tag) ? "primary" : "default"}
                    onClick={() => handleTagToggle(tag)}
                    sx={{ 
                      mb: isMobile ? 0 : 1,
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                      height: { xs: '28px', sm: '32px' }
                    }}
                  />
                ))}
              </Box>
            </Box>
            
            {/* Destinations grid */}
            {filteredDestinations.length > 0 ? (
              <Grid container spacing={3}>
                {filteredDestinations.map((destination) => (
                  <Grid item xs={12} sm={6} md={4} key={destination.id}>
                    <StyledCard onClick={() => handleCardClick(destination.id)}>
                      <CardMedia
                        component="img"
                        height={220}
                        image={destination.image}
                        alt={destination.name}
                      />
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography 
                            variant="h6" 
                            component="h2" 
                            fontWeight="bold"
                            gutterBottom
                            sx={{
                              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }
                            }}
                          >
                            {destination.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Rating 
                              value={destination.rating} 
                              precision={0.1} 
                              size="small" 
                              readOnly 
                            />
                          </Box>
                        </Box>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          paragraph
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
                            lineHeight: 1.5
                          }}
                        >
                          {destination.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}>
                            Best time: {destination.bestTime || 'Year-round'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <LocalOfferIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}>
                            Price: {destination.priceLevel || '$$'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {destination.tags.map((tag, index) => (
                            <Chip 
                              key={index} 
                              label={tag} 
                              size="small" 
                              color={selectedTags.includes(tag) ? "primary" : "default"} 
                              sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' } }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                px: 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 1
              }}>
                <InfoOutlinedIcon sx={{ fontSize: { xs: '2.5rem', sm: '3rem' }, color: 'text.secondary', mb: 2 }} />
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }
                  }}
                >
                  No destinations found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Try adjusting your search terms or filters to find what you're looking for.
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTags([]);
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default DestinationsPage; 